import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Input,
  Tooltip,
  Field,
  FieldLabel,
  FieldError,
  Modal,
  Drawer,
} from '../../../../components/ui';
import { useAuthStore } from '../../../../store/auth';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import { Court, courtFormSchema, type CourtFormData, hasAcademyManagementRole } from '@court-sync/shared';
import { useSubscriptionCheck } from '../../../../hooks/useSubscriptionCheck';
import { useHasMultiLocation } from '../../../../hooks/useHasMultiLocation';
import { useCourts, useLocations } from '../../../../hooks/useDataStore';
import { locationService } from '../../../../services/location';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { CourtCard } from './CourtCard';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { AlertTriangle, MapPin, Pencil, Trash2 } from 'lucide-react';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';

export default function LocationCourts() {
  const { locationId } = useParams<{ locationId: string }>();
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const navigate = useNavigate();
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const { hasClubManagement } = useClubModuleAccess();
  const { courts, isLoading: loading, createCourt, updateCourt, deleteCourt, refresh } = useCourts(
    currentAcademy?.id
  );
  const hasMultiLocation = useHasMultiLocation();
  const { locations, isLoading: locationsListLoading, refresh: refreshLocations } = useLocations(
    currentAcademy?.id,
    { includeInactive: true, enabled: hasMultiLocation }
  );

  const [submitting, setSubmitting] = useState(false);
  const [editingCourt, setEditingCourt] = useState<Court | null>(null);
  const [showForm, setShowForm] = useState(false);
  const location = useMemo(
    () => (locationId ? locations.find((l) => l.id === locationId) ?? null : null),
    [locations, locationId]
  );
  const locationLoading = hasMultiLocation && locationsListLoading;
  const [showLocationEditModal, setShowLocationEditModal] = useState(false);
  const [locEditName, setLocEditName] = useState('');
  const [locEditAddress, setLocEditAddress] = useState('');
  const [locEditPlaceId, setLocEditPlaceId] = useState('');
  const [locationSaving, setLocationSaving] = useState(false);
  const [locationDeleteModalOpen, setLocationDeleteModalOpen] = useState(false);
  const [locationDeleteSubmitting, setLocationDeleteSubmitting] = useState(false);

  const openLocationEditModal = () => {
    if (!location) return;
    setLocEditName(location.name);
    setLocEditAddress(location.address || location.googleMapsAddress || '');
    setLocEditPlaceId(location.googleMapsPlaceId || '');
    setShowLocationEditModal(true);
  };

  const closeLocationEditModal = () => {
    setShowLocationEditModal(false);
  };

  const saveLocationDetails = async () => {
    if (!currentAcademy || !location || !locationId) return;
    const name = locEditName.trim();
    if (!name) {
      toast.error('Enter a location name');
      return;
    }
    try {
      setLocationSaving(true);
      const addr = locEditAddress.trim();
      await locationService.updateLocation(location.id, {
        academyId: currentAcademy.id,
        name,
        address: addr || undefined,
        googleMapsAddress: addr || undefined,
        googleMapsPlaceId: locEditPlaceId.trim() || undefined,
        isActive: location.isActive,
        sortOrder: location.sortOrder,
      });
      await refreshLocations();
      closeLocationEditModal();
      toast.success('Location updated');
    } catch (e: unknown) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to update location');
    } finally {
      setLocationSaving(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CourtFormData>({
    resolver: zodResolver(courtFormSchema),
    defaultValues: {
      isActive: true,
      availableForHire: false,
      locationId: locationId ?? '',
    },
  });

  useEffect(() => {
    if (locationId) {
      reset({
        name: '',
        surfaceType: undefined,
        courtType: undefined,
        isActive: true,
        availableForHire: false,
        locationId,
      });
    }
  }, [locationId, reset]);

  useEffect(() => {
    if (!user || !hasAcademyManagementRole(user)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!hasMultiLocation && currentAcademy) {
      navigate('/admin/courts', { replace: true });
    }
  }, [hasMultiLocation, currentAcademy, navigate]);

  const courtsAtLocation = useMemo(() => {
    if (!locationId) return [];
    return courts
      .filter((c) => c.locationId === locationId)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
  }, [courts, locationId]);

  const cancelLocationDelete = () => setLocationDeleteModalOpen(false);

  const confirmDeleteLocationPage = async () => {
    if (!currentAcademy || !location || !locationId) return;

    try {
      setLocationDeleteSubmitting(true);
      await locationService.deleteLocation(location.id, currentAcademy.id);
      await refresh();
      toast.success('Location removed');
      setLocationDeleteModalOpen(false);
      navigate('/admin/courts');
    } catch (e: unknown) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to delete location');
    } finally {
      setLocationDeleteSubmitting(false);
    }
  };

  const openLocationDeleteModal = () => setLocationDeleteModalOpen(true);

  const onSubmit = async (data: CourtFormData) => {
    if (!currentAcademy || !locationId) {
      toast.error('No academy or location');
      return;
    }

    try {
      setSubmitting(true);

      if (editingCourt) {
        await updateCourt(editingCourt.id, {
          ...data,
          locationId,
          ...(hasClubManagement ? { availableForHire: data.availableForHire ?? false } : {}),
        });
        toast.success('Court updated successfully');
      } else {
        await createCourt({
          academyId: currentAcademy.id,
          name: data.name!,
          surfaceType: data.surfaceType || undefined,
          courtType: data.courtType || undefined,
          isActive: data.isActive!,
          ...(hasClubManagement ? { availableForHire: data.availableForHire ?? false } : {}),
          locationId,
        });
        toast.success('Court created successfully');
      }

      await refresh();
      handleCancelEdit();
    } catch (error: unknown) {
      console.error('Error saving court:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save court');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (court: Court) => {
    setEditingCourt(court);
    setShowForm(true);
    reset({
      name: court.name,
      surfaceType: court.surfaceType,
      courtType: court.courtType,
      isActive: court.isActive,
      availableForHire: court.availableForHire ?? false,
      locationId,
    });
  };

  const handleDelete = async (court: Court) => {
    if (!confirm(`Are you sure you want to delete "${court.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteCourt(court.id);
      toast.success('Court deleted successfully');
      await refresh();
    } catch (error: unknown) {
      console.error('Error deleting court:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete court');
    }
  };

  const handleDuplicate = async (court: Court) => {
    try {
      setSubmitting(true);

      await createCourt({
        academyId: court.academyId,
        name: `${court.name} - Duplicate`,
        surfaceType: court.surfaceType,
        courtType: court.courtType,
        isActive: court.isActive,
        ...(hasClubManagement ? { availableForHire: court.availableForHire ?? false } : {}),
        locationId: court.locationId ?? locationId,
      });

      toast.success('Court duplicated successfully');
      await refresh();
    } catch (error: unknown) {
      console.error('Error duplicating court:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to duplicate court');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCourt(null);
    setShowForm(false);
    reset({
      name: '',
      surfaceType: undefined,
      courtType: undefined,
      isActive: true,
      availableForHire: false,
      locationId: locationId ?? '',
    });
  };

  if (!user || !hasAcademyManagementRole(user)) {
    return null;
  }

  if (!currentAcademy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600 dark:text-gray-400">Invalid location</p>
      </div>
    );
  }

  if (locationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!location) {
    return (
      <div className="min-h-screen">
        <div className="contents">
          <PageHeader
            title="Location not found"
            breadcrumbs={[{ label: 'Courts', href: '/admin/courts' }]}
          />
          <Card>
            <CardContent className="py-12 text-center text-gray-600 dark:text-gray-400">
              Location not found or you no longer have access.
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const locationTitle = location.name;
  const locationSubtitle = [location.address || location.googleMapsAddress].filter(Boolean).join(' · ');
  const locationDeleteCourtCount = courtsAtLocation.length;

  return (
    <div className="min-h-screen">
      <div className="contents">
        <PageHeader
          title={locationTitle}
          help={locationSubtitle || 'Courts at this location'}
          breadcrumbs={[{ label: 'Courts', href: '/admin/courts' }]}
        />

        <Modal
          isOpen={locationDeleteModalOpen && hasMultiLocation}
          onClose={cancelLocationDelete}
          title="Remove this location?"
          maxWidth="lg"
        >
          {location && (
            <div className="space-y-4">
              <div
                role="alert"
                className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
              >
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                <div className="space-y-2 min-w-0">
                  <p className="font-semibold">This action is permanent and cannot be undone.</p>
                  {locationDeleteCourtCount > 0 ? (
                    <p>
                      Removing &quot;{location.name}&quot; deletes this location and permanently removes{' '}
                      <strong>
                        {locationDeleteCourtCount} court{locationDeleteCourtCount === 1 ? '' : 's'}
                      </strong>{' '}
                      assigned to this site. Bookings and schedules that reference those courts may be affected.
                    </p>
                  ) : (
                    <p>This will remove the venue &quot;{location.name}&quot; from your academy.</p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={cancelLocationDelete}>
                  Cancel
                </Button>
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={confirmDeleteLocationPage}
                    isLoading={locationDeleteSubmitting}
                    disabled={!hasValidSubscription}
                  >
                    Remove location
                  </Button>
                </Tooltip>
              </div>
            </div>
          )}
        </Modal>

        <Card className="mb-8">
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Location details</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Address for this site. Courts for this location are listed below.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openLocationEditModal}
                  disabled={!hasValidSubscription}
                >
                  <Pencil className="h-4 w-4 mr-1.5" aria-hidden />
                  Edit location
                </Button>
              </Tooltip>
              <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openLocationDeleteModal}
                  disabled={!hasValidSubscription || locationDeleteSubmitting}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 border-red-200 dark:border-red-900/50"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" aria-hidden />
                  Delete location
                </Button>
              </Tooltip>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <span className="font-medium text-gray-900 dark:text-white">Name</span>
              <p className="text-gray-700 dark:text-gray-300 mt-0.5">{location.name}</p>
            </div>
            <div>
              <span className="font-medium text-gray-900 dark:text-white">Address</span>
              <p className="text-gray-700 dark:text-gray-300 mt-0.5 whitespace-pre-wrap">
                {location.address || location.googleMapsAddress || '—'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Drawer
          isOpen={showLocationEditModal}
          onClose={closeLocationEditModal}
          title="Edit location"
          width="lg"
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeLocationEditModal}>
                Cancel
              </Button>
              <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                <Button
                  type="button"
                  onClick={saveLocationDetails}
                  isLoading={locationSaving}
                  disabled={!hasValidSubscription}
                >
                  Save
                </Button>
              </Tooltip>
            </div>
          }
        >
          <div className="space-y-4">
            <Field>
              <FieldLabel htmlFor="edit-loc-name">Location name *</FieldLabel>
              <Input
                id="edit-loc-name"
                value={locEditName}
                onChange={(e) => setLocEditName(e.target.value)}
                placeholder="Location name"
              />
            </Field>
            <AddressAutocomplete
              label="Address"
              value={locEditAddress}
              onChange={(v) => {
                setLocEditAddress(v);
                setLocEditPlaceId('');
              }}
              onPlaceResolved={(p) => {
                setLocEditAddress(p.formattedAddress);
                setLocEditPlaceId(p.placeId ?? '');
              }}
              placeholder="Start typing to search with Google"
            />
          </div>
        </Drawer>

        {showForm && (
          <Card className="mb-8">
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingCourt ? 'Edit Court' : 'Create New Court'}
              </h2>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field data-invalid={!!errors.name?.message}>
                    <FieldLabel htmlFor="loc-court-name">Court Name *</FieldLabel>
                    <Input
                      id="loc-court-name"
                      aria-invalid={!!errors.name?.message}
                      {...register('name')}
                      placeholder="Court 1"
                    />
                    <FieldError>{errors.name?.message}</FieldError>
                  </Field>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Surface Type
                    </label>
                    <select {...register('surfaceType')} className="input">
                      <option value="">Select surface type</option>
                      <option value="hard">Hard Court</option>
                      <option value="clay">Clay Court</option>
                      <option value="grass">Grass Court</option>
                      <option value="indoor">Indoor Court</option>
                    </select>
                    {errors.surfaceType && (
                      <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.surfaceType.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Court Type</label>
                    <select {...register('courtType')} className="input">
                      <option value="">Select court type</option>
                      <option value="singles">Singles Only</option>
                      <option value="doubles">Doubles Only</option>
                      <option value="both">Singles & Doubles</option>
                    </select>
                    {errors.courtType && (
                      <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.courtType.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2 space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        {...register('isActive')}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Available for lesson scheduling
                      </span>
                    </label>

                    {hasClubManagement ? (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          {...register('availableForHire')}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Available for hire
                        </span>
                      </label>
                    ) : null}
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <Button type="button" variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                    <Button type="submit" isLoading={submitting} disabled={!hasValidSubscription}>
                      {editingCourt ? 'Update Court' : 'Create Court'}
                    </Button>
                  </Tooltip>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {!showForm && (
          <>
            {(courtsAtLocation.length > 0 || !loading) && (
              <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button variant="default" onClick={() => setShowForm(true)} disabled={!hasValidSubscription}>
                    + New Court
                  </Button>
                </Tooltip>
              </div>
            )}

            {loading && courtsAtLocation.length === 0 ? (
              <Card>
                <CardContent>
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                </CardContent>
              </Card>
            ) : courtsAtLocation.length === 0 ? (
              <DashboardEmptyStateCard>
                <DashboardEmptyStateIcon icon={MapPin} />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No courts yet</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Create your first court at this location</p>
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button onClick={() => setShowForm(true)} disabled={!hasValidSubscription}>
                    Create Court
                  </Button>
                </Tooltip>
              </DashboardEmptyStateCard>
            ) : (
              <div className="space-y-4 pt-2">
                {courtsAtLocation.map((court) => (
                  <CourtCard
                    key={court.id}
                    court={court}
                    hasValidSubscription={hasValidSubscription}
                    tooltipMessage={tooltipMessage}
                    submitting={submitting}
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    layout="default"
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
